import { Flex } from '@chakra-ui/react';
import { MCustomSelectProps } from '@monetize/types/app';
import { CurrencyCodes, SUPPORTED_CURRENCIES } from '@monetize/types/common';
import React, { useEffect, useState } from 'react';
import { useCurrencies } from '../../hooks/useCurrencies';
import { MainSearchInputV2 } from './MCustomSelect/components/MainSearchInputV2';
import { MCustomSelect } from './MCustomSelect/MCustomSelect';
import MText from './MText';

interface MCurrencySelectProps extends Omit<
  MCustomSelectProps,
  | 'items'
  | 'itemTitle'
  | 'itemValue'
  | 'loading'
  | 'defaultValue'
  | 'showQueryInput'
> {
  useUnselectedCurrencies?: boolean;
}

const MCurrencySelect = React.forwardRef<any, MCurrencySelectProps>(
  ({ useUnselectedCurrencies, ...rest }: MCurrencySelectProps, ref) => {
    const {
      currencies,
      defaultCurrency,
      isLoading: isLoadingCurrencies,
    } = useCurrencies();
    const [unselectedCurrencies, setUnselectedCurrencies] = useState<
      {
        code: string;
        description: string;
        searchTerm: string;
      }[]
    >(currencies);

    useEffect(() => {
      if (!useUnselectedCurrencies) {
        return;
      }
      const selectedCurrencies = new Set(currencies.map(({ code }) => code));
      setUnselectedCurrencies(
        Object.keys(SUPPORTED_CURRENCIES)
          .filter(
            (currency) => !selectedCurrencies.has(currency as CurrencyCodes),
          )
          .map((currency) => {
            const { code, name } =
              SUPPORTED_CURRENCIES[currency as CurrencyCodes];
            return {
              code,
              description: name,
              searchTerm: `${code}${name}`.toLowerCase(),
            };
          }),
      );
    }, [currencies, useUnselectedCurrencies]);

    return (
      <MCustomSelect
        placeholder="Currency"
        ref={ref}
        items={!useUnselectedCurrencies ? currencies : unselectedCurrencies}
        itemTitle="code"
        itemValue="code"
        loading={isLoadingCurrencies}
        defaultValue={defaultCurrency}
        useMainInputAsSearchInput
        MainInputComponent={MainSearchInputV2}
        itemSearch="searchTerm"
        renderItemContent={({ title, item, isSubtitle }) => {
          return (
            <Flex alignItems="center">
              <MText
                color="inherit"
                fontWeight="bold"
                truncate
                lineClamp={1}
                fontSize={isSubtitle ? '12px' : 'sm'}
                mr="2"
              >
                {title}
              </MText>
              <MText
                fontWeight={'normal'}
                color="inherit"
                truncate
                lineClamp={1}
                fontSize={isSubtitle ? '12px' : 'sm'}
              >
                {item.description}
              </MText>
            </Flex>
          );
        }}
        {...rest}
      />
    );
  },
);

export default MCurrencySelect;
